import { io, type Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/authStore';
import { resetAccountNotifyPrefs } from '@/lib/accountNotifyPrefs';
import type { ServerToClientEvents } from '@bunker/shared/socketEvents.js';

let singleton: Socket | null = null;
let disposeRetry: (() => void) | null = null;

const TEMPORARY_AUTH_RETRY_DELAYS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];

function bindTemporaryAuthRetry(socket: Socket): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let attempts = 0;
  let enabled = false;
  let temporaryDisconnect = false;
  let unsubscribeAuth: (() => void) | null = null;
  const connect = socket.connect.bind(socket);
  const disconnect = socket.disconnect.bind(socket);

  const cancel = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    temporaryDisconnect = false;
    attempts = 0;
  };
  const stop = () => {
    enabled = false;
    cancel();
  };
  const schedule = () => {
    const delay = TEMPORARY_AUTH_RETRY_DELAYS[attempts];
    if (!enabled || timer !== null || delay === undefined) return;
    attempts = Math.min(attempts + 1, TEMPORARY_AUTH_RETRY_DELAYS.length - 1);
    timer = setTimeout(() => {
      timer = null;
      // Namespace rejection/disconnect destroys Socket.IO's subscriptions.
      // Transport errors still have native reconnection; do not compete with it.
      if (enabled && singleton === socket && !socket.connected && !socket.active) connect();
    }, delay);
  };
  const onConnect = () => cancel();
  const onConnectError = (error: Error) => {
    if (error.message === 'temporarily_unavailable') schedule();
    else if (socket.active) cancel(); // Native transport reconnection remains eligible.
    else stop();
  };
  const onTemporaryUnavailable: ServerToClientEvents['auth:temporarily-unavailable'] = () => {
    if (enabled) temporaryDisconnect = true;
  };
  const onDisconnect = (reason: string) => {
    if (reason === 'io server disconnect' && temporaryDisconnect) {
      temporaryDisconnect = false;
      schedule();
    } else {
      cancel();
      if (reason === 'io server disconnect' || reason === 'io client disconnect') enabled = false;
    }
  };

  socket.connect = () => {
    if (!enabled) cancel();
    enabled = true;
    unsubscribeAuth ??= useAuthStore.subscribe((state) => {
      if (!state.user || state.status === 'unauthenticated') stop();
    });
    return connect();
  };
  socket.disconnect = () => {
    // An already-rejected socket emits no disconnect event on explicit logout.
    stop();
    unsubscribeAuth?.();
    unsubscribeAuth = null;
    return disconnect();
  };
  socket.on('connect', onConnect);
  socket.on('connect_error', onConnectError);
  socket.on('auth:temporarily-unavailable', onTemporaryUnavailable);
  socket.on('disconnect', onDisconnect);
  socket.on('session:revoked', stop);
  return () => {
    stop();
    unsubscribeAuth?.();
    unsubscribeAuth = null;
    socket.off('connect', onConnect);
    socket.off('connect_error', onConnectError);
    socket.off('auth:temporarily-unavailable', onTemporaryUnavailable);
    socket.off('disconnect', onDisconnect);
    socket.off('session:revoked', stop);
  };
}

export function isMockMode(): boolean {
  // Window.__BUNKER_MOCK__ is declared globally in vite-env.d.ts so the
  // `as unknown as { ... }` widening is no longer needed.
  return typeof window !== 'undefined' && window.__BUNKER_MOCK__ === true;
}

/**
 * Lazy Socket.io client (singleton). Cookie-based auth — server reads
 * bunker_session from the handshake. In dev Vite proxies /ws to the API.
 *
 * In `?mock=full` preview mode we still create the io() instance (so all
 * `emit / on / off` calls type-check and no-op safely) but never auto-
 * connect — the SPA's repeated calls to `.connect()` are overridden into
 * no-ops so we don't spam ws://localhost:4001 with rejected upgrades.
 */
export function getSocket(): Socket {
  if (singleton) return singleton;
  // Vite loads `.env` for every mode. A developer's local
  // VITE_WS_URL=http://localhost:4001 must therefore never be allowed into a
  // production bundle: in the desktop shell that would point at the user's
  // own PC and leave the UI reconnecting forever. Production is deliberately
  // same-origin; only the dev server may override the websocket host.
  const wsUrl =
    import.meta.env.DEV && import.meta.env.VITE_WS_URL
      ? import.meta.env.VITE_WS_URL
      : window.location.origin;
  singleton = io(wsUrl, {
    path: '/ws',
    withCredentials: true,
    autoConnect: false,
    transports: ['websocket'],
  });
  if (isMockMode()) {
    // Patch the public connect / disconnect surface. The socket still
    // exposes `.emit`, `.on`, etc. — emits are dropped, on-handlers
    // simply never fire.
    const noop = () => singleton!;
    singleton.connect = noop;
    singleton.disconnect = noop;
  } else {
    disposeRetry = bindTemporaryAuthRetry(singleton);
  }
  return singleton;
}

export function disconnectSocket() {
  // Sign-out or a foreign account in the frame: the old account's notification
  // settings must not outlive its socket.
  resetAccountNotifyPrefs();
  if (singleton) {
    disposeRetry?.();
    disposeRetry = null;
    singleton.disconnect();
    singleton = null;
  }
}
